'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, Info, XCircle, type LucideIcon } from 'lucide-react';

export type ModalVariant = 'info' | 'success' | 'error' | 'warning' | 'confirm';

interface AlertOptions {
  title?: string;
  variant?: ModalVariant;
  confirmText?: string;
}
interface ConfirmOptions extends AlertOptions {
  cancelText?: string;
}

interface ModalApi {
  /** Substitui window.alert: exibe um modal com um botão e resolve ao fechar. */
  showAlert: (message: React.ReactNode, opts?: AlertOptions) => Promise<void>;
  /** Substitui window.confirm: resolve true (confirmar) ou false (cancelar/fechar). */
  showConfirm: (message: React.ReactNode, opts?: ConfirmOptions) => Promise<boolean>;
}

interface ModalState {
  open: boolean;
  message: React.ReactNode;
  title?: string;
  variant: ModalVariant;
  confirmText: string;
  cancelText?: string; // presente => modo confirm (mostra botão cancelar)
  resolve?: (result: boolean) => void;
}

const ModalContext = createContext<ModalApi | null>(null);

export function useModal(): ModalApi {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal deve ser usado dentro de <ModalProvider>');
  return ctx;
}

const VARIANTS: Record<ModalVariant, { icon: LucideIcon; ring: string; btn: string }> = {
  info: { icon: Info, ring: 'bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400', btn: 'bg-blue-600 hover:bg-blue-700' },
  success: { icon: CheckCircle2, ring: 'bg-green-100 text-green-600 dark:bg-green-950/60 dark:text-green-400', btn: 'bg-green-600 hover:bg-green-700' },
  error: { icon: XCircle, ring: 'bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400', btn: 'bg-red-600 hover:bg-red-700' },
  warning: { icon: AlertTriangle, ring: 'bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400', btn: 'bg-amber-600 hover:bg-amber-700' },
  confirm: { icon: HelpCircle, ring: 'bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400', btn: 'bg-blue-600 hover:bg-blue-700' },
};

const DEFAULT_TITLES: Record<ModalVariant, string> = {
  info: 'Aviso',
  success: 'Sucesso',
  error: 'Erro',
  warning: 'Atenção',
  confirm: 'Confirmação',
};

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState>({
    open: false,
    message: '',
    variant: 'info',
    confirmText: 'OK',
  });

  const close = useCallback((result: boolean) => {
    setState((s) => {
      s.resolve?.(result);
      return { ...s, open: false, resolve: undefined };
    });
  }, []);

  const showAlert = useCallback(
    (message: React.ReactNode, opts: AlertOptions = {}) =>
      new Promise<void>((resolve) => {
        setState({
          open: true,
          message,
          title: opts.title,
          variant: opts.variant ?? 'info',
          confirmText: opts.confirmText ?? 'OK',
          cancelText: undefined,
          resolve: () => resolve(),
        });
      }),
    [],
  );

  const showConfirm = useCallback(
    (message: React.ReactNode, opts: ConfirmOptions = {}) =>
      new Promise<boolean>((resolve) => {
        setState({
          open: true,
          message,
          title: opts.title,
          variant: opts.variant ?? 'confirm',
          confirmText: opts.confirmText ?? 'Confirmar',
          cancelText: opts.cancelText ?? 'Cancelar',
          resolve,
        });
      }),
    [],
  );

  // Fecha com ESC (cancela) e trava o scroll do body enquanto aberto.
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [state.open, close]);

  const isConfirm = state.cancelText !== undefined;
  const v = VARIANTS[state.variant];
  const title = state.title ?? DEFAULT_TITLES[state.variant];

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {state.open && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => close(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-black/5 dark:ring-white/10 animate-[modalIn_.15s_ease-out]">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${v.ring}`}
                >
                  <v.icon className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
                  <div className="mt-1 whitespace-pre-line break-words text-sm text-gray-600 dark:text-slate-300">
                    {state.message}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 rounded-b-2xl bg-gray-50 dark:bg-slate-800/50 px-6 py-4">
              {isConfirm && (
                <button
                  type="button"
                  onClick={() => close(false)}
                  className="rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  {state.cancelText}
                </button>
              )}
              <button
                type="button"
                autoFocus
                onClick={() => close(true)}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${v.btn}`}
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}
